import { useState, useEffect, useRef } from 'react';
import { Api } from '../api';
import { CompanyApi } from '../api/companyApi';
import { isSuperAdmin, isSuperAndSubAdmin } from '../utils/helper';

// Global cache to persist icons across component instances
let cachedIcons = null;
let fetchPromise = null;

/**
 * Custom hook to fetch salary sacrifice icons
 * - Fetches icons only once and caches them
 * - Calls Api.getSalarySacrificeIcons for Super Admin/Sub Admin
 * - Calls CompanyApi.getSalarySacrificeIcons for Company Admin
 * - Returns loading state and icons list
 */
const useSalarySacrificeIcons = () => {
  const [icons, setIcons] = useState(cachedIcons || []);
  const [loading, setLoading] = useState(!cachedIcons);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const fetchIcons = async () => {
      // Determine which API to use based on user role
      const isSuperOrSubAdmin = isSuperAndSubAdmin();

      // If we already have cached icons, use them
      if (cachedIcons) {
        setIcons(cachedIcons);
        setLoading(false);
        return;
      }

      // If fetch is already in progress, wait for it
      if (fetchPromise) {
        try {
          const result = await fetchPromise;
          if (isMounted.current) {
            setIcons(result);
            setLoading(false);
          }
        } catch (err) {
          if (isMounted.current) {
            setError(err.message || 'Failed to fetch icons');
            setLoading(false);
          }
        }
        return;
      }

      // Start fetching icons
      setLoading(true);

      // Choose API based on user role
      const apiMethod = isSuperOrSubAdmin
        ? Api.getSalarySacrificeIcons
        : CompanyApi.getSalarySacrificeIcons;

      fetchPromise = apiMethod()
        .then((response) => {
          const rawIconList = response?.data?.data || [];
          console.log('Raw icon data from API:', rawIconList);
          // Map API response to expected format
          const iconList = rawIconList.map((icon) => ({
            _id: icon.icon, // Use 'icon' as the unique identifier
            icon_url: icon.imageUrl, // Map 'imageUrl' to 'icon_url'
            name: icon.icon // Use 'icon' as the name
          }));
          console.log('Transformed icon data:', iconList);
          cachedIcons = iconList;
          return iconList;
        })
        .catch((err) => {
          console.error('Error fetching salary sacrifice icons:', err);
          throw err;
        })
        .finally(() => {
          fetchPromise = null;
        });

      try {
        const iconList = await fetchPromise;
        if (isMounted.current) {
          setIcons(iconList);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted.current) {
          setError(err.message || 'Failed to fetch icons');
          setLoading(false);
          setIcons([]);
        }
      }
    };

    fetchIcons();

    return () => {
      isMounted.current = false;
    };
  }, []);

  return { icons, loading, error };
};

export default useSalarySacrificeIcons;
